export const implicitFieldsToRemove: Array<string> = [
  'password',
  'token',
  'email',
  'authorization',
]

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const deepClone = <T>(input: T, seen = new WeakMap()): T => {
  if (Array.isArray(input)) {
    return input.map((item) => deepClone(item, seen)) as T
  }

  if (!isRecord(input)) {
    return input
  }

  if (seen.has(input)) {
    return seen.get(input) as T
  }

  const output: Record<string, unknown> = {}
  seen.set(input, output)

  Object.keys(input).forEach((key) => {
    output[key] = deepClone(input[key], seen)
  })

  return output as T
}

export const checkIfObject = (o: unknown): o is Record<string, unknown> =>
  isRecord(o)

export const trimPropertiesFromObject = (
  obj: Record<string, unknown>,
  properties: Array<string> = [],
  visited = new WeakSet<object>()
): { [key: string]: any } => {
  if (visited.has(obj)) {
    return obj
  }

  visited.add(obj)

  properties = [...properties, ...implicitFieldsToRemove].map((p) =>
    p.toLowerCase()
  )

  Object.keys(obj).forEach((key) => {
    if (properties.includes(key.toLowerCase())) {
      delete obj[key]
      return
    }

    const value = obj[key]
    if (checkIfObject(value)) {
      obj[key] = trimPropertiesFromObject(value, properties, visited)
    }
  })

  return obj
}

export const redactEmailAddress = (
  email = '',
  maskSensitiveData = true
): string => {
  if (typeof email !== 'string' || email.length === 0) {
    return ''
  }

  if (!email.includes('@')) {
    // this is not an email, so return back the string
    return email
  }

  if (!maskSensitiveData) {
    return email
  }

  const delimiter = '...'
  const emailSplit = email.split('@')

  let firstPart = emailSplit[0]

  if (firstPart.length >= 4) {
    firstPart =
      firstPart.substring(0, 2) +
      delimiter +
      firstPart.substring(firstPart.length - 2, firstPart.length)
  }

  return `${firstPart}@${emailSplit[1]}`
}
